import { motion } from "framer-motion";
import { cn } from "../../lib/cn";
import type { PlanEvent } from "../../lib/types";

interface Props {
  plan: PlanEvent | null;
}

export function PlanProgressBar({ plan }: Props) {
  if (!plan || plan.items.length === 0) return null;

  const total = plan.total;
  const done = plan.items.filter((i) => i.status === "done").length;
  const partial = plan.items.filter((i) => i.status === "partial").length;
  const active = plan.items.filter((i) => i.status === "active").length;
  const percent = total > 0 ? Math.round(((done + partial * 0.5) / total) * 100) : 0;
  const isComplete = done === total;

  return (
    <div className="space-y-2">
      {/* Compact bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-bg-subtle overflow-hidden relative">
          <motion.div
            className={cn(
              "h-full rounded-full",
              isComplete ? "bg-green-500" : "bg-accent-500",
            )}
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          {active > 0 && !isComplete && (
            <motion.div
              className="absolute top-0 h-full bg-accent-400/40 rounded-full"
              style={{ left: `${percent}%` }}
              animate={{ width: ["0%", `${100 / total}%`, "0%"] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
        </div>
        <span className={cn(
          "text-small font-medium tabular-nums",
          isComplete ? "text-green-400" : "text-fg-muted",
        )}>
          {done}/{total}
        </span>
      </div>

      {/* Step dots */}
      <div className="flex gap-1">
        {plan.items.map((item) => (
          <div
            key={item.index}
            className={cn(
              "flex-1 h-1 rounded-full transition-colors duration-300",
              item.status === "done" && "bg-green-500",
              item.status === "active" && "bg-accent-500 animate-pulse",
              item.status === "partial" && "bg-yellow-500",
              item.status === "skipped" && "bg-fg-subtle/30",
              item.status === "pending" && "bg-bg-subtle",
            )}
            title={`${item.index}. ${item.title} (${item.status})`}
          />
        ))}
      </div>
    </div>
  );
}
